import React, { useEffect } from 'react';
import { useLocation, Link } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Icon from '../components/common/Icon';
import Breadcrumbs from '../components/common/Breadcrumbs';
import NewsCard from '../components/common/NewsCard';
import SectionHeader from '../components/common/SectionHeader';

const themeData = {
  lavoro: {
    title: 'Lavoro e Dignità',
    overline: 'Politiche Economiche',
    description: "Il lavoro è la base della nostra cittadinanza, ma per troppi giovani oggi è sinonimo di sfruttamento, precariato e incertezza. Lottiamo per un'Italia dove il merito sia riconosciuto e la dignità protetta.",
    image: 'https://images.unsplash.com/photo-1521737711867-e3b97375f902?q=80&w=1600&auto=format&fit=crop',
    icon: 'Briefcase',
    color: 'var(--color-primary)',
    points: [
      'Salario Minimo a 9 euro l\'ora',
      'Abolizione degli stage gratuiti',
      'Incentivi reali all\'occupazione giovanile stabile',
      'Settimana lavorativa di 4 giorni a parità di salario'
    ]
  },
  ambiente: {
    title: 'Ambiente e Clima',
    overline: 'Futuro Sostenibile',
    description: 'La crisi climatica è la sfida più urgente della nostra generazione. Non c’è giustizia sociale senza giustizia ambientale. Vogliamo una transizione che non lasci indietro nessuno.',
    image: 'https://images.unsplash.com/photo-1441974231531-c6227db76b6e?q=80&w=1600&auto=format&fit=crop',
    icon: 'Leaf',
    color: '#10b981',
    points: [
      'Stop ai sussidi ambientalmente dannosi',
      'Potenziamento massiccio del trasporto pubblico',
      'Investimenti in comunità energetiche rinnovabili',
      'Protezione del suolo e rimboschimento urbano'
    ]
  }
};

const TematicaPage = () => {
  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const id = query.get('id') || 'lavoro';
  const data = themeData[id] || themeData.lavoro;

  const breadcrumbs = [
    { label: 'Home', path: '/' },
    { label: 'Tematiche', path: '/temi' },
    { label: data.title }
  ];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  return (
    <Layout>
      <section className="hero--epic">
        <div className="hero__bg">
          <div className="hero__image" style={{ backgroundImage: `url(${data.image})` }}></div>
          <div className="hero__overlay" style={{ background: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.8) 100%)' }}></div>
        </div>
        
        <div className="hero__content-epic container" style={{ color: '#fff' }}>
          <div className="reveal" style={{ marginBottom: 'var(--space-8)' }}>
            <Breadcrumbs items={breadcrumbs} style={{ color: 'rgba(255,255,255,0.7)' }} />
          </div>
          
          <div className="reveal mb-4">
            <span className="badge" style={{ background: `${data.color}`, color: '#fff', border: 'none' }}>{data.overline}</span>
          </div>
          
          <h1 className="hero__title-epic text-reveal-trigger" style={{ color: '#fff' }}>
            <span className="line text-reveal"><span className="word text-reveal__inner">{data.title}</span></span>
          </h1>
          
          <p className="hero__subtitle-epic reveal" style={{ color: 'rgba(255,255,255,0.9)', maxWidth: '800px' }}>
            {data.description}
          </p>
        </div>
      </section>

      <section className="section bg-subtle">
        <div className="container">
          <div className="grid grid-2 gap-12 items-center">
            <div className="reveal">
              <SectionHeader overline="Le nostre proposte" title="Punti chiave" className="mb-8" />
              <ul style={{ listStyle: 'none', padding: 0 }}>
                {data.points.map((point, i) => (
                  <li key={i} className="flex items-start gap-4 mb-6 reveal stagger-2">
                    <div style={{ 
                      width: '32px', 
                      height: '32px', 
                      borderRadius: '50%', 
                      background: `${data.color}20`, 
                      color: data.color, 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      flexShrink: 0
                    }}>
                      <Icon name="Check" size={18} />
                    </div>
                    <span className="text-body font-medium">{point}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="reveal reveal--scale">
              <div style={{ 
                background: '#fff', 
                padding: 'var(--space-10)', 
                borderRadius: 'var(--radius-3xl)',
                boxShadow: 'var(--shadow-2xl)',
                border: '1px solid var(--color-border-subtle)'
              }}>
                <h3 className="h3 mb-6">Attivati su questo tema</h3>
                <p className="text-body-sm text-secondary mb-8">
                  Ogni nostra proposta nasce dal confronto nei circoli. Unisciti a noi per dare forza a queste battaglie.
                </p>
                <div className="flex flex-col gap-4">
                  <Link to="/partecipa" className="btn btn--primary btn--lg w-full">Iscriviti ai GD</Link>
                  <Link to="/contatti" className="btn btn--outline btn--lg w-full">Chiedi informazioni</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container">
          <SectionHeader overline="Dal campo" title="Ultime notizie collegate" className="mb-10" />
          <div className="grid grid-3">
            <NewsCard 
              title="Salario Minimo: i GD in piazza per la dignità" 
              category="Lavoro" 
              date="12 Mar 2024"
              excerpt="Centinaia di giovani hanno manifestato oggi per chiedere l'approvazione immediata della proposta di legge..."
              image="https://images.unsplash.com/photo-1540317580384-e5d43616b9aa?q=80&w=800&auto=format&fit=crop"
            />
            <NewsCard 
              title="Ambiente: ecco il report dei GD sulla transizione" 
              category="Ambiente" 
              date="05 Mar 2024"
              excerpt="Presentato oggi il documento programmatico per un'Italia a emissioni zero entro il 2050..."
              image="https://images.unsplash.com/photo-1470219556762-1771e7f9427d?q=80&w=800&auto=format&fit=crop"
            />
            <NewsCard 
              title="Assemblea Nazionale: i temi al centro del dibattito" 
              category="Politica" 
              date="01 Mar 2024"
              excerpt="Conclusa l'assemblea che ha definito le priorità per i prossimi mesi..."
              image="https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=800&auto=format&fit=crop"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default TematicaPage;
